import { v4 as uuidv4 } from 'uuid';

import { getMessageTime } from 'src/utils/chat';
import { CHAT_GRETTINGS } from 'src/utils/constants';

import {
  ERole,
  EResponseType,
  type EFeedback,
  type FileWithId,
  type TChatHistory,
} from 'src/types';

export const initialChatHistoryState: TChatHistory = {
  primaryConversationId: null,
  secondaryConversationId: null,
  documentId: null,
  runId: null,
  messages: [],
  isLoading: false,
};

type ChatAction =
  | { type: 'SET_ERROR'; error: string }
  | {
      type: 'SET_USER_MESSAGE';
      message: string;
      file?: FileWithId[];
      url?: string[];
      model?: string;
      knowledgeBase?: string[];
      allFiles?: FileWithId[];
    }
  | { type: 'SET_LOADING_MESSAGE'; message: string }
  | {
      type: 'UPDATE_ASSISTANT_RESPONSE';
      answer: string;
      isFileResponse?: boolean;
      model?: string;
      knowledgeBase?: string[];
    }
  | { type: 'UPDATE_FEEDBACK'; vote: EFeedback; id: string }
  | { type: 'SET_RUN_ID'; runId: string }
  | { type: 'SET_INTENT_ID'; intentId: string }
  | { type: 'SET_DOCUMENT_ID'; documentId: string[] }
  | { type: 'CLEAR_HISTORY' }
  | { type: 'REMOVE_FILE_FROM_CONTEXT'; id: string }
  | { type: 'SET_LOADING'; isLoading: boolean };

export type ChatActionPayload<T extends ChatAction['type']> = Omit<
  Extract<ChatAction, { type: T }>,
  'type'
>;

export function chatHistoryReducer(state: TChatHistory, action: ChatAction): TChatHistory {
  switch (action.type) {
    case 'SET_ERROR': {
      return {
        ...state,
        messages: state.messages.map((message, index) =>
          index === state.messages.length - 1
            ? {
                ...message,
                content: action.error,
                loading: false,
                timestamp: getMessageTime(),
                type: EResponseType.ERROR,
              }
            : message
        ),
      };
    }
    case 'SET_USER_MESSAGE': {
      // Add user message with loading
      const { file, message, url, model, knowledgeBase, allFiles } = action;

      return {
        ...state,
        documentId: state.documentId,
        messages: [
          ...state.messages,

          {
            id: uuidv4(),
            role: ERole.USER,
            content: message,
            file,
            type: file ? EResponseType.FILE : EResponseType.TEXT,
            timestamp: getMessageTime(),
            conversationId: state.primaryConversationId || state.secondaryConversationId,
            url,
            allFiles,
          },
          {
            id: uuidv4(),
            role: ERole.ASSISTANT,
            loading: true,
            content: CHAT_GRETTINGS.LOADING_REGULAR_TEXT,
            type: file ? EResponseType.FILE : EResponseType.TEXT,
            timestamp: getMessageTime(),
            conversationId: state.primaryConversationId || state.secondaryConversationId,
            model,
            knowledgeBase,
          },
        ],
      };
    }
    case 'UPDATE_ASSISTANT_RESPONSE':
      // Update the last message with the actual assistant response
      return {
        ...state,
        messages: [
          ...state.messages.map((message, index) =>
            index === state.messages.length - 1
              ? {
                  ...message,
                  content: action.answer,
                  loading: false,
                  timestamp: getMessageTime(),
                  isFileResponse: action.isFileResponse,
                  model: action.model,
                  knowledgeBase: action.knowledgeBase,
                }
              : message
          ),
        ],
      };
    case 'UPDATE_FEEDBACK': {
      return {
        ...state,
        messages: [
          ...state.messages.map((message) => {
            if (message.id === action.id) {
              return { ...message, feedback: { vote: action.vote } };
            }
            return message;
          }),
        ],
      };
    }
    case 'SET_RUN_ID': {
      return {
        ...state,
        runId: action.runId,
      };
    }
    case 'SET_INTENT_ID': {
      return {
        ...state,
        intentId: action.intentId,
      };
    }
    case 'SET_DOCUMENT_ID': {
      if (Array.isArray(action.documentId) && action.documentId.length > 0) {
        return {
          ...state,
          documentId: [
            ...(Array.isArray(state.documentId) ? state.documentId : []),
            ...action.documentId,
          ],
        }
      }
      return {
        ...state,
        documentId: action.documentId,
      };
    }
    case 'CLEAR_HISTORY': {
      return {
        ...state,
        isLoading: false, // avoid loading from previous message
        messages: [],
      };
    }
    case 'REMOVE_FILE_FROM_CONTEXT': {
      const { id } = action;
      return {
        ...state,
        messages: state.messages.map((message) => {
          if (message.file && message.file?.filter((eachFile) => eachFile.id === id).length > 0) {
            return {
              ...message,
              file: message.file.map((eachFile) =>
                eachFile.id === id ? { ...eachFile, id: '' } : eachFile
              ),
            };
          }
          return message;
        }),
      };
    }
    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.isLoading,
      };
    }
    default:
      return state;
  }
}
